import React, { useMemo } from "react";
import { CalendarDays, HeartHandshake } from "lucide-react";
import CollapsibleCard from "@/components/ui/CollapsibleCard";
import ListItem from "@/components/ui/ListItem";
import { formatDateRange } from "@/lib/date-utils";
import { formatSmartDate } from "@/lib/date";

function formatVisitTime(timeStr) {
  if (!timeStr) return null;
  const parts = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (!parts) return null;
  const h = parseInt(parts[1], 10);
  const m = parts[2];
  return `${h % 12 || 12}:${m} ${h >= 12 ? "PM" : "AM"}`;
}
import { getLocalDateString } from "@/lib/date";
import { getDateKey, getDateValue } from "@/features/dashboard/utils";

const SOURCE_CONFIG = {
  Personal: { iconColor: "text-violet-600" },
  Iglesia:  { iconColor: "text-teal-600"   },
  Campo:    { iconColor: "text-amber-600"  },
};

function UpcomingAgenda({
  upcomingPersonalEvents,
  upcomingMinisterialEvents,
  upcomingVisits,
  navigate,
}) {
  const allEvents = useMemo(() => {
    const todayStr = getLocalDateString();
    const isOngoing = (e) => {
      const start = getDateKey(e);
      return start && start < todayStr && e.end_date && e.end_date.slice(0, 10) >= todayStr;
    };
    return [...upcomingPersonalEvents, ...upcomingMinisterialEvents]
      .sort((a, b) => {
        const aOn = isOngoing(a) ? 0 : 1;
        const bOn = isOngoing(b) ? 0 : 1;
        if (aOn !== bOn) return aOn - bOn;
        return getDateValue(getDateKey(a)) - getDateValue(getDateKey(b));
      })
      .slice(0, 5);
  }, [upcomingPersonalEvents, upcomingMinisterialEvents]);

  return (
    <div className="px-4">
      <CollapsibleCard
        defaultOpen={true}
        title={
          <span className="flex items-center gap-2">
            <CalendarDays size={15} className="text-blue-500 dark:text-blue-400" />
            Esta semana
          </span>
        }
      >

        {/* ── EVENTOS (personal + ministerial, orden cronológico) ── */}
        {allEvents.map((e) => {
          const cfg = e._source === "Personal" ? SOURCE_CONFIG.Personal
                    : e.church_id             ? SOURCE_CONFIG.Iglesia
                    :                           SOURCE_CONFIG.Campo;
          const dateStr = formatDateRange(e.date || e.start_date, e.end_date);
          return (
            <ListItem
              key={`${e._source}-${e.id}`}
              icon={<CalendarDays size={16} className={cfg.iconColor} />}
              title={e.title}
              subtitle={e._source ? `${e._source === "Campo" ? "Asoc." : e._source} · ${dateStr}` : dateStr}
              onClick={() => navigate("/agenda")}
            />
          );
        })}

        {/* ── VISITAS ─────────────────────────────────────── */}
        {upcomingVisits.map((v) => (
          <ListItem
            key={`uv-${v.id}`}
            icon={<HeartHandshake size={16} className="text-blue-600" />}
            title={v.contact_name}
            subtitle={[
              formatSmartDate(v.date || v.visit_date),
              formatVisitTime(v.time),
              "Visita",
            ].filter(Boolean).join(" · ")}
            onClick={() => navigate(`/visits/${v.id}`)}
          />
        ))}

        {/* ── VACÍO ───────────────────────────────────────── */}
        {allEvents.length === 0 && upcomingVisits.length === 0 && (
          <p className="text-[14px] text-slate-400 dark:text-slate-500 py-1">
            Esta semana está tranquila.
          </p>
        )}

      </CollapsibleCard>
    </div>
  );
}

export default React.memo(UpcomingAgenda);
